<script setup lang="ts">
import { computed } from 'vue';
import IdentifierLabel from '@/components/IdentifierLabel/IdentifierLabel.vue';
import { SetUserStateUserStateEnum, UserDto } from '@/api/generator';
import Dialog from '@/components/Dialog/Dialog.vue';
import { api } from '@/api';
import { useLoading } from '@/utils/useLoading';
import { useSendActivationLink } from '@/views/accounts/utils.ts';
import PtwCancelButton from '@/components/buttons/PtwCancelButton.vue';
import { SHOW_USER_ACTIVATION } from '@/env';

export interface SendActivationLinkModalProps {
  user: UserDto | null;
}

const props = defineProps<SendActivationLinkModalProps>();
const emit = defineEmits<{
  'update:user': [user: null];
  onSent: [];
}>();

const { sendActivationLink } = useSendActivationLink();

function close() {
  emit('update:user', null);
}

const iuid = computed(() => `AC${props.user?.id.toString().padStart(5, '0')}`);

const { action: activateUser, loading: isActivating } = useLoading(async () => {
  if (props.user) {
    await api.usersApi.setUserState({
      id: props.user.id,
      userState: SetUserStateUserStateEnum.ActivationPending,
    });
    await api.usersApi.setUserState({
      id: props.user.id,
      userState: SetUserStateUserStateEnum.Activated,
    });
  }
  emit('onSent');
  close();
});

const { action: sendActivationLinkToUser, loading } = useLoading(async () => {
  if (props.user) {
    await sendActivationLink(props.user);
    emit('onSent');
    close();
  }
});

function handleOpen(shouldOpen: boolean) {
  if (!shouldOpen) {
    close();
  }
}
</script>

<template>
  <Dialog :modelValue="Boolean(props.user)" :maxWidth="480" @update:modelValue="handleOpen">
    <template #title>
      {{ $t('SendActivationLink') }}
    </template>

    <template #text>
      <IdentifierLabel :identifier="iuid" :label="props.user?.fullName"></IdentifierLabel>
      <p class="mt-5">
        {{ $t('ActivationInfoMsg') }}
      </p>
      <p class="mt-5">
        {{ $t('ActivationPossibleInfoMsg') }}
      </p>
    </template>

    <template #actions>
      <PtwCancelButton :text="$t('SendLater')" @click="close"> </PtwCancelButton>

      <TextVBtn
        v-if="SHOW_USER_ACTIVATION"
        color="primary"
        :loading="isActivating"
        size="default"
        @click="activateUser"
      >
        activate
      </TextVBtn>

      <PrimaryVBtn
        variant="elevated"
        color="secondary"
        size="default"
        :loading="loading"
        :text="$t('SendNow')"
        @click="sendActivationLinkToUser()"
      />
    </template>
  </Dialog>
</template>
